package dev.danko.roblox_craft_ai.uikit.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.shared.model.AssetTitles
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.icons.AppIcon
import dev.danko.roblox_craft_ai.uikit.icons.Icon
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.attributes.InputType

/** A work's name with a pencil: Enter saves the new name, Escape or leaving the field without a change cancels. */
@Composable
fun EditableTitle(title: String, onRename: suspend (String) -> Unit, modifier: Modifier = Modifier) {
    val s = strings()
    val palette = LocalAppTheme.current.colors
    val scope = rememberCoroutineScope()
    var editing by remember { mutableStateOf(false) }
    var draft by remember(title) { mutableStateOf(title) }
    var saving by remember { mutableStateOf(false) }

    fun save() {
        val name = AssetTitles.clean(draft)
        if (name == null || name == title) { editing = false; draft = title; return }
        saving = true
        scope.launch {
            try {
                onRename(name)
                editing = false
            } catch (error: Throwable) {
                ToastManager.error(error.message ?: s.home.renameFailed)
            } finally {
                saving = false
            }
        }
    }

    Row(modifier.gap(6.px).minWidth(0.px), verticalAlignment = Alignment.CenterVertically) {
        if (editing) {
            Input(
                type = InputType.Text,
                attrs = Modifier.flexGrow(1).minWidth(0.px).fontSize(17.px).fontWeight(FontWeight.Bold).padding(6.px, 10.px)
                    .borderRadius(8.px).border(1.px, LineStyle.Solid, palette.accent).backgroundColor(palette.background)
                    .color(palette.textPrimary).styleModifier { property("font-family", "inherit") }
                    .toAttrs {
                        value(draft)
                        attr("maxlength", AssetTitles.MAX_LENGTH.toString())
                        attr("aria-label", s.home.rename)
                        // Focused after the click that opened it is over, or that click's end takes the focus away.
                        ref { field ->
                            val timer = kotlinx.browser.window.setTimeout({ field.focus(); field.select() }, 0)
                            onDispose { kotlinx.browser.window.clearTimeout(timer) }
                        }
                        if (saving) attr("disabled", "true")
                        onInput { draft = it.value }
                        onKeyDown { event ->
                            when (event.key) {
                                "Enter" -> { event.preventDefault(); save() }
                                "Escape" -> { event.stopPropagation(); editing = false; draft = title }
                            }
                        }
                        onBlur { if (!saving) save() }
                    }
            )
        } else {
            SpanText(
                title,
                Modifier.fontSize(18.px).fontWeight(FontWeight.Bold).color(palette.textPrimary).minWidth(0.px)
                    .styleModifier { property("overflow", "hidden"); property("text-overflow", "ellipsis"); property("white-space", "nowrap") }
            )
            Button(attrs = Modifier.padding(4.px).borderRadius(6.px).border(0.px).backgroundColor(palette.background)
                .color(palette.textSecondary).cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer).flexShrink(0)
                .toAttrs {
                    attr("type", "button")
                    attr("title", s.home.rename)
                    attr("aria-label", s.home.rename)
                    onClick { editing = true }
                }) { Icon(AppIcon.PENCIL, size = 15) }
        }
    }
}
